import { cn } from "@thilke/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

const lancerCounterVariants = cva("grid gap-2", {
  variants: {
    orientation: {
      horizontal: "",
      vertical: "w-fit",
    },
  },
  defaultVariants: {
    orientation: "horizontal",
  },
});

const lancerCounterPipVariants = cva(
  "shrink-0 border transition-all duration-150",
  {
    variants: {
      size: {
        sm: "size-2.5",
        default: "size-3.5",
        lg: "size-5",
      },
      tone: {
        default:
          "data-[filled=true]:border-primary data-[filled=true]:bg-primary data-[filled=true]:shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]",
        accent:
          "data-[filled=true]:border-accent data-[filled=true]:bg-accent data-[filled=true]:shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]",
        success:
          "data-[filled=true]:border-success data-[filled=true]:bg-success data-[filled=true]:shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]",
        warning:
          "data-[filled=true]:border-warning data-[filled=true]:bg-warning data-[filled=true]:shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]",
        destructive:
          "data-[filled=true]:border-destructive data-[filled=true]:bg-destructive data-[filled=true]:shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]",
        info: "data-[filled=true]:border-info data-[filled=true]:bg-info data-[filled=true]:shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]",
        muted:
          "data-[filled=true]:border-muted-foreground data-[filled=true]:bg-muted-foreground",
      },
      filled: {
        true: "",
        false:
          "border-border/70 bg-muted/20 shadow-[inset_0_1px_2px_rgba(0,0,0,0.08)]",
      },
    },
    defaultVariants: {
      filled: false,
      size: "default",
      tone: "default",
    },
  },
);

type LancerCounterTone = NonNullable<
  VariantProps<typeof lancerCounterPipVariants>["tone"]
>;
type LancerCounterSize = NonNullable<
  VariantProps<typeof lancerCounterPipVariants>["size"]
>;

export interface LancerCounterProps
  extends Omit<React.ComponentProps<"div">, "children">,
    VariantProps<typeof lancerCounterVariants> {
  value: number;
  max: number;
  label?: React.ReactNode;
  tone?: LancerCounterTone;
  size?: LancerCounterSize;
  showValue?: boolean;
  valueLabel?: React.ReactNode;
  interactive?: boolean;
  onPipClick?: (index: number) => void;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function LancerCounter({
  value,
  max,
  label,
  tone = "default",
  size = "default",
  orientation = "horizontal",
  showValue = true,
  valueLabel,
  interactive = false,
  onPipClick,
  className,
  ...props
}: LancerCounterProps) {
  const normalizedMax = Math.max(0, Math.floor(max));
  const normalizedValue = clamp(Math.floor(value), 0, normalizedMax);
  const pips = Array.from({ length: normalizedMax }, (_, index) => index + 1);
  const displayValue = valueLabel ?? `${normalizedValue}/${normalizedMax}`;

  return (
    <div
      data-slot="lancer-counter"
      data-variant="pips"
      data-orientation={orientation}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={normalizedMax}
      aria-valuenow={normalizedValue}
      aria-label={typeof label === "string" ? label : undefined}
      className={cn(lancerCounterVariants({ orientation }), className)}
      {...props}
    >
      {(label || showValue) && (
        <div className="flex min-w-0 items-center justify-between gap-2 text-xs leading-none">
          {label ? (
            <span
              data-slot="lancer-counter-label"
              className="truncate font-mono font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {label}
            </span>
          ) : (
            <span />
          )}
          {showValue ? (
            <span
              data-slot="lancer-counter-value"
              className="font-mono font-semibold text-foreground"
            >
              {displayValue}
            </span>
          ) : null}
        </div>
      )}
      <div
        data-slot="lancer-counter-pips"
        className={cn("flex gap-1", orientation === "vertical" && "flex-col")}
      >
        {pips.map((pip) => (
          <LancerCounterPip
            key={pip}
            filled={pip <= normalizedValue}
            tone={tone}
            size={size}
            interactive={interactive}
            onClick={() => onPipClick?.(pip)}
          />
        ))}
      </div>
    </div>
  );
}

function LancerCounterPip({
  className,
  filled = false,
  tone = "default",
  size = "default",
  interactive = false,
  onClick,
  ...props
}: Omit<React.ComponentProps<"button">, "onClick"> &
  VariantProps<typeof lancerCounterPipVariants> & {
    interactive?: boolean;
    onClick?: () => void;
  }) {
  const pipClass = cn(
    lancerCounterPipVariants({ filled, tone, size }),
    interactive &&
      "cursor-pointer hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
    className,
  );
  const pipStyle = {
    clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
  };
  if (interactive) {
    return (
      <button
        type="button"
        data-slot="lancer-counter-pip"
        data-filled={filled ? "true" : "false"}
        aria-label="Set counter value"
        className={pipClass}
        style={pipStyle}
        onClick={onClick}
        {...props}
      />
    );
  }
  return (
    <button
      type="button"
      data-slot="lancer-counter-pip"
      data-filled={filled ? "true" : "false"}
      tabIndex={-1}
      aria-hidden="true"
      disabled
      className={pipClass}
      style={pipStyle}
      {...props}
    />
  );
}

export type { LancerCounterSize, LancerCounterTone };
export {
  LancerCounter,
  LancerCounterPip,
  lancerCounterPipVariants,
  lancerCounterVariants,
};
